import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Shirt, Printer, Clock, ArrowRight, Check, Trophy, Award, Gift } from 'lucide-react';

const ServicesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      id: 'engraving',
      name: 'Custom Engraving',
      slug: '/services/engraving',
      icon: Sparkles,
      description: 'Precision laser and rotary engraving on metal, wood, glass, crystal, acrylic, and more. Perfect for awards, plaques, and personalized gifts. Same-day engraving available in San Diego.',
      features: ['Laser Engraving', 'Rotary Engraving', 'Same-Day Service', 'Color Fill Options'],
      image: 'https://images.unsplash.com/photo-1615286922420-c6b348ffbd62?crop=entropy&cs=srgb&fm=jpg&q=85&w=800'
    },
    {
      id: 'trophies',
      name: 'Custom Trophies',
      slug: '/services/trophies',
      icon: Trophy,
      description: 'San Diego\'s largest selection of sports trophies, corporate awards, championship trophies, and participation awards. Best prices guaranteed with fast turnaround.',
      features: ['Sports Trophies', 'Corporate Awards', 'Championship Cups', 'Rush Available'],
      image: 'https://customer-assets.emergentagent.com/job_hospitality-hub-303/artifacts/f08oactp_unnamed%20%288%29.webp'
    },
    {
      id: 'plaques',
      name: 'Custom Plaques',
      slug: '/services/plaques',
      icon: Award,
      description: 'Beautiful wood, acrylic, and metal plaques for recognition, memorials, and awards. Perpetual plaques, photo plaques, and custom designs available.',
      features: ['Wood Plaques', 'Acrylic Plaques', 'Perpetual Plaques', 'Photo Plaques'],
      image: 'https://images.unsplash.com/photo-1576723420195-6ac9d4c71fe8?crop=entropy&cs=srgb&fm=jpg&q=85&w=800'
    },
    {
      id: 'embroidery',
      name: 'Embroidery',
      slug: '/services/embroidery',
      icon: Shirt,
      description: 'Professional embroidery for apparel, hats, bags, and more. Durable, high-quality stitching that represents your brand beautifully.',
      features: ['Logo Digitizing', 'Multi-Color Designs', 'Bulk Orders', 'Quick Turnaround'],
      image: 'https://images.unsplash.com/photo-1557462987-dd1c191266f0?crop=entropy&cs=srgb&fm=jpg&q=85&w=800'
    },
    {
      id: 'printing',
      name: 'Screen Printing',
      slug: '/services/screen-printing',
      icon: Printer,
      description: 'Full-color screen printing on apparel, promotional products, and marketing materials. From small runs to large orders.',
      features: ['Screen Printing', 'Digital Printing', 'Sublimation', 'UV Printing'],
      image: 'https://images.unsplash.com/photo-1773525912476-213bff96b8a4?crop=entropy&cs=srgb&fm=jpg&q=85&w=800'
    },
    {
      id: 'promotional',
      name: 'Promotional Products',
      slug: '/services/promotional-products',
      icon: Gift,
      description: 'Branded merchandise for trade shows, corporate events, and marketing campaigns. Pens, drinkware, bags, tech accessories, and more.',
      features: ['Corporate Gifts', 'Trade Show Items', 'Branded Merch', 'Custom Keychains'],
      image: 'https://images.unsplash.com/photo-1637635753380-20bf6f46ede0?crop=entropy&cs=srgb&fm=jpg&q=85&w=800'
    },
    {
      id: 'rush',
      name: 'Rush Services',
      slug: '/contact',
      icon: Clock,
      description: 'Need it fast? We offer same-day and next-day turnaround for urgent orders. Call us at (619) 640-1633 to discuss your timeline.',
      features: ['Same-Day Available', 'Next-Day Options', 'Priority Production', 'Emergency Orders'],
      image: 'https://images.unsplash.com/photo-1706374503312-7a4a4c030d2d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800'
    }
  ];

  return (
    <div data-testid="services-page" className="min-h-screen pt-28 pb-20 bg-white">
      {/* Hero Section */}
      <section className="max-w-6xl mx-auto px-6 py-16 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs uppercase tracking-widest text-[#D4AF37] mb-3 font-semibold">San Diego Trophy & Engraving Services</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-[#1a365d] mb-4 font-bold">Full-Service Trophy Shop & Custom Engraving in San Diego</h1>
          <p className="text-base text-gray-600 max-w-3xl mx-auto mb-6">
            RC Awards is San Diego&apos;s premier full-service trophy shop and custom engraving company. From design to delivery, we handle every aspect of your award, plaque, and promotional product needs. With nearly 30 years of experience serving San Diego County, we combine quality craftsmanship with exceptional customer service.
          </p>
          <p className="text-sm text-gray-500 max-w-2xl mx-auto">
            Whether you need <Link to="/services/trophies" className="text-[#D4AF37] hover:underline">custom trophies</Link>, <Link to="/services/plaques" className="text-[#D4AF37] hover:underline">award plaques</Link>, <Link to="/services/engraving" className="text-[#D4AF37] hover:underline">laser engraving</Link>, or <Link to="/services/embroidery" className="text-[#D4AF37] hover:underline">embroidery services</Link>, our team delivers fast turnaround with the best prices guaranteed.
          </p>
        </motion.div>
      </section>

      {/* Services Grid */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="space-y-20">
          {services.map((service, index) => {
            const Icon = service.icon;
            const isEven = index % 2 === 0;
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-10 items-center`}
              >
                <div className={`${!isEven ? 'lg:order-2' : ''}`}>
                  <div className="relative rounded-2xl overflow-hidden h-72 lg:h-80 shadow-xl group">
                    <img
                      src={service.image}
                      alt={`${service.name} - RC Awards San Diego`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1a365d]/50 to-transparent"></div>
                  </div>
                </div>
                <div className={`${!isEven ? 'lg:order-1' : ''}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-[#D4AF37] rounded-xl flex items-center justify-center">
                      <Icon size={24} className="text-[#1a365d]" />
                    </div>
                    <h2 className="text-2xl font-bold text-[#1a365d]">{service.name}</h2>
                  </div>
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    {service.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Check size={16} className="text-[#D4AF37]" />
                        <span className="text-sm text-gray-600">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Link
                    to={service.slug}
                    className="inline-flex items-center gap-2 text-[#1a365d] font-medium hover:text-[#D4AF37] transition-colors"
                  >
                    Learn More <ArrowRight size={16} />
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Additional Services */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl text-[#1a365d] mb-4 font-bold">Additional Engraving & Customization Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Beyond our core services, RC Awards offers a complete range of customization options including sublimation printing, sandblasting, hot stamping, and more. Whatever your recognition or branding needs, we have the equipment and expertise to deliver.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              'Sublimation Printing',
              'Sandblasting',
              'Hot Stamping',
              'Pad Printing',
              'Heat Transfer',
              'Vinyl Cutting',
              'Name Badges',
              'Gift Wrapping'
            ].map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-4 text-center border border-gray-100 hover:border-[#D4AF37]/30 hover:shadow-md transition-all duration-300"
              >
                <Check size={20} className="text-[#D4AF37] mx-auto mb-2" />
                <span className="text-sm text-gray-700 font-medium">{service}</span>
              </motion.div>
            ))}
          </div>

          {/* Service Areas */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <p className="text-sm text-gray-500">
              <strong className="text-[#1a365d]">Serving all of San Diego County:</strong> Downtown San Diego, La Mesa, El Cajon, Chula Vista, National City, Coronado, La Jolla, Pacific Beach, North Park, Hillcrest, Mission Valley, Santee, Poway, Escondido, and surrounding areas.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Choose RC Awards for Your Services */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl text-[#1a365d] mb-6 font-bold">Why San Diego Businesses Choose RC Awards</h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Since 1995, RC Awards has been the trusted choice for San Diego businesses, schools, sports leagues, and organizations needing quality trophies, custom engraving, and promotional products. Our commitment to craftsmanship, competitive pricing, and outstanding customer service sets us apart.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Check size={20} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-[#1a365d]">Same-Day Rush Services</p>
                    <p className="text-sm text-gray-500">Need it fast? We offer same-day and next-day turnaround for urgent orders.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check size={20} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-[#1a365d]">Best Prices Guaranteed</p>
                    <p className="text-sm text-gray-500">We match or beat any competitor&apos;s price on comparable products.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check size={20} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-[#1a365d]">Free Design Assistance</p>
                    <p className="text-sm text-gray-500">Our experienced team helps you create the perfect award or product.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check size={20} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-[#1a365d]">Local Delivery Available</p>
                    <p className="text-sm text-gray-500">Free delivery for larger orders within San Diego County.</p>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="bg-[#1a365d] rounded-2xl p-6 text-center text-white">
                <p className="text-4xl font-bold text-[#D4AF37]">5.0</p>
                <p className="text-sm text-white/70">Google Rating</p>
              </div>
              <div className="bg-gray-50 rounded-2xl p-6 text-center">
                <p className="text-4xl font-bold text-[#1a365d]">200+</p>
                <p className="text-sm text-gray-500">5-Star Reviews</p>
              </div>
              <div className="bg-gray-50 rounded-2xl p-6 text-center">
                <p className="text-4xl font-bold text-[#1a365d]">1995</p>
                <p className="text-sm text-gray-500">Established</p>
              </div>
              <div className="bg-[#D4AF37] rounded-2xl p-6 text-center">
                <p className="text-4xl font-bold text-[#1a365d]">4 Days</p>
                <p className="text-sm text-[#1a365d]/70">Standard Turnaround</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-xs uppercase tracking-widest text-[#D4AF37] mb-3 font-semibold">How It Works</p>
            <h2 className="text-3xl text-[#1a365d] mb-4 font-bold">Our Simple Process</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Contact Us', desc: 'Tell us about your project and requirements' },
              { step: '02', title: 'Get a Quote', desc: 'Receive a detailed quote with pricing options' },
              { step: '03', title: 'Approve Design', desc: 'Review and approve your proof before production' },
              { step: '04', title: 'Receive Order', desc: 'Pick up or have it delivered to your door' }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-[#1a365d] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-[#D4AF37] font-bold text-xl">{item.step}</span>
                </div>
                <h3 className="font-semibold text-[#1a365d] mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1a365d] py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl text-white mb-4 font-bold">Have a Project in Mind?</h2>
            <p className="text-white/70 mb-8 max-w-lg mx-auto">
              Contact us today to discuss your recognition needs. We&apos;ll provide a free quote and help you choose the perfect solution for your San Diego business, school, or organization.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
              <Link
                to="/contact"
                className="bg-[#D4AF37] text-[#1a365d] px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-[#c9a432] transition-colors inline-flex items-center justify-center gap-2"
              >
                Get a Free Quote
                <ArrowRight size={14} />
              </Link>
              <a
                href="tel:6196401633"
                className="bg-white/10 border border-white/30 text-white px-7 py-3.5 rounded-full text-sm font-medium hover:bg-white/20 transition-colors inline-flex items-center justify-center gap-2"
              >
                Call (619) 640-1633
              </a>
            </div>
            {/* Internal Links for SEO */}
            <div className="pt-6 border-t border-white/20">
              <p className="text-white/50 text-sm mb-3">Explore Our Services:</p>
              <div className="flex flex-wrap justify-center gap-3">
                <Link to="/services/trophies" className="text-xs text-white/70 hover:text-[#D4AF37] transition-colors">Trophies</Link>
                <span className="text-white/30">•</span>
                <Link to="/services/plaques" className="text-xs text-white/70 hover:text-[#D4AF37] transition-colors">Plaques</Link>
                <span className="text-white/30">•</span>
                <Link to="/services/engraving" className="text-xs text-white/70 hover:text-[#D4AF37] transition-colors">Engraving</Link>
                <span className="text-white/30">•</span>
                <Link to="/services/embroidery" className="text-xs text-white/70 hover:text-[#D4AF37] transition-colors">Embroidery</Link>
                <span className="text-white/30">•</span>
                <Link to="/services/screen-printing" className="text-xs text-white/70 hover:text-[#D4AF37] transition-colors">Screen Printing</Link>
                <span className="text-white/30">•</span>
                <Link to="/services/promotional-products" className="text-xs text-white/70 hover:text-[#D4AF37] transition-colors">Promotional Products</Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
